import { getBoolean } from '../utilities';
import * as htmx from "htmx.org";

export function bindToggles(content?: Element | Document | null | undefined) {
    // look for [data-toggle].
    //  if data-toggle == open, the toggle only opens the target
    //  if data-toggle == dismiss, the toggle ony dismisses the target
    // [data-target] sets the id of the element to toggle. use "," as a separator if you need to toggle more than one element.
    // [data-expanded] (set to true or false) gives current shown/hidden status of the element
    // [data-reload-dismiss] (set to true or false) tells the browser to reload when the element is dismissed.
    // [data-toggle-group] (set all items in a group to the same value) all items in the group will be dismissed except for the element being toggled.
    // [data-scrolltop] (set to true, false, or omit) if true, puts the toggled element at the top of the page.
    // [data-event] (set to js event or omit) defaults to click.
    // [data-blur-toggle] if present, closes the toggled element when a click is registered outside of the element
    if (!content) {
        content = document;
    }
    content.querySelectorAll<HTMLElement>("[data-toggle]").forEach(toggle => {
        const tEvent = toggle.getAttribute('toggle-event');

        if (tEvent === null || tEvent !== 'true') {
            toggle.setAttribute('toggle-event', 'true');
            if (toggle.getAttribute('aria-controls') === null) {
                toggle.setAttribute('aria-controls', toggle.getAttribute('data-target') ?? "");
            }

            toggle.addEventListener(toggle.getAttribute('data-event') ?? "click", evt => {
                evt.preventDefault();
                toggleEvent(toggle);
            });

            if (toggle.hasAttribute("data-blur-toggle")) {
                document.addEventListener("click", e => checkForBlurToggle(toggle, e));
                document.addEventListener("touchstart", e => checkForBlurToggle(toggle, e));
            }
        }
    });
    content.querySelectorAll<HTMLDialogElement>('[data-expanded="true"]').forEach(d => showElement(d, "hidden"));
}

function checkForBlurToggle(toggle: HTMLElement, event: Event) {
    const toggleTarget = document.getElementById(toggle.getAttribute("data-target") ?? "");
    if (toggle.getAttribute("aria-expanded") === "true" && event.target !== toggle && event.target as HTMLElement !== toggleTarget) {
        toggleEvent(toggle);
    }
}

function toggleEvent(elem: HTMLElement) {
    toggleElement(elem);
    toggleGroup(elem, elem.getAttribute("data-toggle-group"));
    const scrollTop = elem.getAttribute('data-scrolltop');
    if (scrollTop !== null && scrollTop === 'true') {
        elem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
}

function toggleElement(elem: HTMLElement, open: boolean | null = null) {
    let targetIds = elem.getAttribute("data-target")?.split(",");
    if (targetIds === undefined) { return };
    open = (open === null) ? getToggleOption(elem.getAttribute('data-toggle')) : open;
    let toggleClass = "hidden"
    if (elem.hasAttribute("data-toggle-class")) {
        let toggleClassAttribute = elem.getAttribute("data-toggle-class");
        toggleClass = toggleClassAttribute ?? "";
    }
    targetIds.forEach(id => {
        let target = document.querySelector<HTMLElement>(`#${id.trim()}`);
        if (target === null) { return };

        const expanded = (open !== null) ? open : !getBoolean(target.getAttribute('data-expanded'))

        elem.setAttribute('aria-expanded', `${expanded}`);

        if (expanded) {
            showElement(target, toggleClass);
        }
        else {
            dismissElement(target, toggleClass, getBoolean(elem.getAttribute('data-reload-dismiss')));
        }
    });
}

function toggleGroup(elem: HTMLElement, group: string | null) {
    if (group === null) {
        return;
    }
    document.querySelectorAll<HTMLElement>(`[data-toggle-group="${group}"]`).forEach(grpElem => {
        if (grpElem === elem) {
            return;
        }
        toggleElement(grpElem, false);
    });
}

export function showElement(target: HTMLElement, toggleClass: string = "hidden") {
    if (target == null) {
        console.debug("No element found to open");
        return;
    }
    if (target.tagName.toLowerCase() === 'dialog') {
        (<HTMLDialogElement>target).showModal();
        return;
    }

    target.setAttribute("data-expanded", "true");
    if (toggleClass && target.classList.contains(toggleClass)) {
        target.classList.remove(toggleClass);
    }
}

export function dismissElement(target: HTMLElement, toggleClass: string = "hidden", reload: boolean = false) {
    if (reload) { location.reload(); }
    if (target == null) {
        console.debug("No element found to dismiss");
        return;
    }
    if (target.tagName.toLowerCase() === 'dialog') {
        (<HTMLDialogElement>target).close();
        return;
    }
    target.setAttribute("data-expanded", "false");
    if (toggleClass) {
        target.classList.add(toggleClass);
    }
}

function getToggleOption(value: string | null) {
    if (value === null) {
        return null;
    }

    if (value.toLowerCase() === 'open') {
        return true;
    }
    else if (value.toLowerCase() === 'dismiss') {
        return false;
    }
    else {
        return null;
    }
}

document.addEventListener('DOMContentLoaded', (event) => {
    bindToggles(document);
});

htmx.onLoad((content) => {
    bindToggles(content)
});
